import { CONFIG } from '~/services/config'
import memberhubConstants from '~/constants/memberhub.constants'
import { expireSession } from '~/utils/auth/session-expiry'

/*
 | ===============================================================================
 | Shared token refresh state — ensures only one refresh is in-flight at a time
 | and all 401 requests wait for it before retrying.
 | ===============================================================================
 */
let refreshPromise = null

/*
 * Outcomes of a refresh attempt. Only `invalid` ends the session: it means the refresh
 * endpoint looked at the refresh token and rejected it (or there was none to send). A
 * `transient` failure — no network, a 5xx, a 429 — says nothing about the token, so the
 * caller gets its original error and the session is kept (CLUB-867 F2).
 */
const REFRESHED = 'refreshed'
const INVALID = 'invalid'
const TRANSIENT = 'transient'

/*
 * Member Hub paths that answer 401 for reasons other than an expired session: an invalid
 * reset/verify link, a bad refresh token, logging out. Refreshing on those would either
 * loop or log a signed-in member out because they opened an old link (CLUB-867 F5).
 * Anchored to the first path segment so `/resend-verification` keeps refreshing.
 */
const REFRESH_EXEMPT_PATH = /^\/?(refresh-token|logout|reset-password|verify|verify-token|forgot-password)(\/|\?|$)/

const ABSOLUTE_URL = /^https?:\/\//i

const originOf = value => {
  try {
    return new URL(value).origin
  } catch {
    return null
  }
}

const readStoredToken = () => {
  try {
    return getLocal('token') ? JSON.parse(getLocal('token')) : {}
  } catch {
    return {}
  }
}

const hasAuthorizationHeader = headers => Object.keys(headers || {}).some(key => key.toLowerCase() === 'authorization')

const pathOf = url => (ABSOLUTE_URL.test(url) ? new URL(url).pathname.replace(/^\/memberhub/, '') : url)

const successResponse = response => ({
  data: response,
  code: response?.code || response?.status || memberhubConstants.API_SUCCESS
})

// $fetch throws on error status codes, catch and return in useFetch-like format
// This allows checkResponse to handle errors properly
const errorResponse = error => {
  const statusCode = error.statusCode || error.status
  return {
    error: {
      value: {
        data: error.data || error.response?._data || {
          code: statusCode || memberhubConstants.API_BAD_REQUEST,
          message: error.message || 'Request failed'
        }
      }
    },
    code: statusCode || memberhubConstants.API_BAD_REQUEST,
    data: error.data || error.response?._data
  }
}

export class ApiService {
  constructor (apiType = 'memberhub') {
    this.apiType = apiType
  }

  /*
     * Global api request service
     * @param method
     * @param url
     * @param data
     * @param config
     * @returns {*}
     */
  request = async (method, url, data = {}, config = {}) => {
    const { environment } = useRuntimeConfig().public
    const serviceBaseUrl = CONFIG.baseUrl(environment, this.apiType)
    /*
         | ===============================================================================
         | Custom logic is applied for request url
         | If full URL is coming as request param, it will be used as request URL
         | otherwise, respective api-service's base url will be prepended with request URL
         |
         | An absolute URL is only honoured on this service's own origin. The bearer token
         | goes out with every request, so a URL built from user input (verify.vue's
         | ?callback=) must never be able to point it at another host (CLUB-944 T1).
         | ===============================================================================
         */
    const forceDirectUrlRequest = ABSOLUTE_URL.test(url)
    if (forceDirectUrlRequest && originOf(url) !== originOf(serviceBaseUrl)) {
      return errorResponse({
        statusCode: memberhubConstants.API_BAD_REQUEST,
        message: 'Request blocked: unexpected host'
      })
    }
    const baseURL = forceDirectUrlRequest ? url : serviceBaseUrl
    const options = {
      method,
      canCancel: config.canCancel,
      params: config.params,
      forceDirectUrlRequest,
      apiType: this.apiType,
      baseURL
    }
    /*
     * Per-call headers. Needed by callers that must authenticate with a token the
     * interceptor cannot see — during the MFA challenge the primary session is suspended,
     * so `localStorage.token` is absent and the valid token is parked in the pending
     * record (see use-mfa-logging.js).
     *
     * NOTE the precedence: api-interceptor.js spreads `options.headers` and THEN writes
     * Authorization from the stored token, so a header passed here only survives while
     * there is no stored token. That is exactly the suspended-session case it exists for;
     * it cannot be used to override a live session's token.
     */
    if (config.headers) {
      options.headers = { ...config.headers }
    }
    /*
     * Per-call fetch credentials, e.g. 'include' so the browser keeps a cookie set by
     * a cross-origin API response. Only set when a caller asks: every other call
     * authenticates with the Bearer token and sends no cookies.
     */
    if (config.credentials) {
      options.credentials = config.credentials
    }
    if (method !== 'GET') {
      options.body = data
    }

    // Read once, before the request: the refresh decision is about the token this request
    // actually went out with, not whatever is in storage by the time the 401 comes back.
    const sentStoredBearer = !!readStoredToken().access_token && !hasAuthorizationHeader(config.headers)

    try {
      // $fetch returns a Promise that resolves to the data directly
      const response = await $fetch(url, options)
      // Return in a format similar to useFetch for consistency
      return successResponse(response)
    } catch (error) {
      if (!this._canRefresh(error, url, sentStoredBearer)) {
        return errorResponse(error)
      }

      const outcome = await this._refreshToken()

      if (outcome === REFRESHED) {
        // Refresh and retry are separate steps: whatever the retry returns is the real
        // answer to this request, a 422 included (CLUB-867 F1). A second 401 is returned
        // too — the token was just accepted by Descope, so this is not a session problem.
        try {
          return successResponse(await $fetch(url, options))
        } catch (retryError) {
          return errorResponse(retryError)
        }
      }

      return errorResponse(error)
    }
  }

  /*
     * Whether a failed request should trigger a silent refresh: a 401 on a Member Hub
     * call that went out with the stored bearer token, on a path that can mean "session
     * expired". Suspended-session callers pass their own Authorization header and must
     * never refresh or expire the primary session (CLUB-867 F14).
     */
  _canRefresh = (error, url, sentStoredBearer) => {
    const statusCode = error.statusCode || error.status
    return statusCode === memberhubConstants.API_TOKEN_EXPIRED &&
      this.apiType === 'memberhub' &&
      import.meta.client &&
      sentStoredBearer &&
      !REFRESH_EXEMPT_PATH.test(pathOf(url))
  }

  /*
     * Handles token refresh with deduplication — only one refresh in-flight at a time.
     * All concurrent 401 requests share the same refresh promise.
     * @returns {Promise<'refreshed'|'invalid'|'transient'>}
     */
  _refreshToken = () => {
    if (!refreshPromise) {
      refreshPromise = this._doRefreshToken()
        .then(outcome => {
          // Expire from the one shared refresh, not from each waiting caller: a burst of
          // 401s must end the session exactly once.
          if (outcome === INVALID) {
            expireSession({ reason: 'refresh_invalid' })
          }
          return outcome
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  _doRefreshToken = async () => {
    const refreshToken = readStoredToken().refresh_token
    if (!refreshToken) {
      return INVALID
    }

    const { environment } = useRuntimeConfig().public
    const baseURL = CONFIG.baseUrl(environment, 'memberhub')

    let res
    try {
      // Call refresh endpoint directly (bypasses the retry logic to avoid recursion)
      res = await $fetch(`/refresh-token/${encodeURIComponent(refreshToken)}`, {
        method: 'POST',
        baseURL,
        apiType: 'memberhub'
      })
    } catch (error) {
      return this._classifyRefreshFailure(error)
    }

    const tokenData = res?.data || res
    if (!tokenData?.access_token) {
      return INVALID
    }

    // Merge with existing token to preserve refresh_token if the response doesn't include a new one
    const existingToken = readStoredToken()
    const mergedToken = {
      ...existingToken,
      ...tokenData,
      // Only overwrite refresh_token if the new one is non-empty
      refresh_token: tokenData.refresh_token || existingToken.refresh_token
    }
    setLocal('token', JSON.stringify(mergedToken))
    useNuxtApp().$nativeAuthPersistence?.set('token', JSON.stringify(mergedToken))
    return REFRESHED
  }

  /*
     * No status means the request never got an answer (offline, aborted, timed out).
     * 5xx and 429 are the server or the limiter, not the token. Everything else is the
     * refresh endpoint rejecting what it was sent.
     */
  _classifyRefreshFailure = error => {
    const statusCode = error.statusCode || error.status
    if (!statusCode || statusCode >= 500 || statusCode === 429) {
      return TRANSIENT
    }
    return INVALID
  }

  /*
     * GET API service request
     * @param url
     * @param config
     * @returns {*}
     */
  get = (url, config = {}) => {
    return this.request('GET', url, {}, config)
  }

  /*
     * POST API service request
     * @param url
     * @param data
     * @param config
     * @returns {*}
     */
  post = (url, data, config = {}) => {
    return this.request('POST', url, data, config)
  }

  /*
     * PUT API service request
     * @param url
     * @param data
     * @param config
     * @returns {*}
     */
  put = (url, data, config = {}) => {
    return this.request('PUT', url, data, config)
  }

  /*
     * PATCH API service request
     * @param url
     * @param data
     * @param config
     * @returns {*}
     */
  patch = (url, data, config = {}) => {
    return this.request('PATCH', url, data, config)
  }

  /*
     * DELETE API service request
     * @param url
     * @param config `config.data` is sent as the request body when present
     * @returns {*}
     */
  delete = (url, config = {}) => {
    return this.request('DELETE', url, config.data || {}, config)
  }
}
